import styled from 'styled-components';

import { ColumnsSectionContainer, SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import {
    SiteSetupState,
    SiteSetupFragmentFragment,
    useForceFinishSiteSetupMutation,
    useFinishSiteSetupWithManualGenerationMutation,
    useAbortSiteSetupMutation,
    TotadminPermission,
} from 'data/__generated__';

import { Loader, LoaderModeType } from 'components/Loader';
import { toast } from 'react-toastify';
import { StateHistory } from 'components/DetailsView/StateHistory';
import { StatusTag } from '../components/StatusTag';

export const ActionSection = ({ siteSetup }: { siteSetup: SiteSetupFragmentFragment }) => {
    const [forceFinishSiteSetup, { loading: forceFinishSiteSetupLoading }] = useForceFinishSiteSetupMutation({
        variables: { siteSetupId: siteSetup._id },
    });
    const [finishSiteSetupWithManualGeneration, { loading: finishSiteSetupWithManualGenerationLoading }] =
        useFinishSiteSetupWithManualGenerationMutation({ variables: { siteSetupId: siteSetup._id } });

    const [abortSiteSetup, { loading: abortSiteSetupLoading }] = useAbortSiteSetupMutation({
        variables: { siteSetupId: siteSetup._id },
    });

    function displayToast({ success, errorMessage }: { success: boolean; errorMessage: string }) {
        if (success) {
            toast.success('Le rangement à bien été validé !');
        } else {
            toast.error(errorMessage);
        }
    }

    async function forceFinish() {
        const { data, errors } = await forceFinishSiteSetup();

        displayToast({
            success: !!data?.forceFinishSiteSetup,
            errorMessage: "Une erreur inconnue s'est produite : " + errors?.join(', '),
        });
    }

    async function manualFinish() {
        const { data, errors } = await finishSiteSetupWithManualGeneration();

        displayToast({
            success: !!data?.finishSiteSetupWithManualGeneration,
            errorMessage: "Une erreur inconnue s'est produite : " + errors?.join(', '),
        });
    }

    async function abort() {
        const { data, errors } = await abortSiteSetup();

        displayToast({
            success: !!data?.abortSiteSetup,
            errorMessage: "Une erreur inconnue s'est produite : " + errors?.join(', '),
        });
    }

    const formattedStateHistory = siteSetup.stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    return (
        <SectionContainer title="Historique de statuts" isInitiallyOpen>
            <ColumnsSectionContainer numberOfColumns={3}>
                <SectionColumn>
                    <StateHistory records={formattedStateHistory} />
                </SectionColumn>
                <SectionColumn>
                    <StatusTag state={siteSetup.state} showHeading />
                </SectionColumn>
                <SectionColumn>
                    <TotemPrimaryButton
                        onClick={manualFinish}
                        requiredPermissions={[TotadminPermission.SiteSetupsManage]}
                        disabled={
                            finishSiteSetupWithManualGenerationLoading ||
                            abortSiteSetupLoading ||
                            siteSetup.state !== SiteSetupState.Created
                        }
                    >
                        Valider le rangement (fait à la main, pas de Totops)
                        {finishSiteSetupWithManualGenerationLoading || abortSiteSetupLoading ? (
                            <Loader mode={LoaderModeType.Spin} size="22px" />
                        ) : null}
                    </TotemPrimaryButton>
                    <TotemPrimaryButton
                        onClick={forceFinish}
                        requiredPermissions={[TotadminPermission.SiteSetupsManage]}
                        disabled={
                            forceFinishSiteSetupLoading ||
                            abortSiteSetupLoading ||
                            siteSetup.state !== SiteSetupState.InProgress
                        }
                    >
                        Valider le rangement (en forçant si besoin)
                        {forceFinishSiteSetupLoading ? <Loader mode={LoaderModeType.Spin} size="22px" /> : null}
                    </TotemPrimaryButton>
                    <TotemPrimaryButton
                        onClick={abort}
                        requiredPermissions={[TotadminPermission.SiteSetupsManage]}
                        disabled={
                            abortSiteSetupLoading ||
                            [SiteSetupState.Finished, SiteSetupState.Aborted].includes(siteSetup.state)
                        }
                    >
                        Annuler le rangement
                    </TotemPrimaryButton>
                </SectionColumn>
            </ColumnsSectionContainer>
        </SectionContainer>
    );
};

const ButtonContainer = styled.div`
    display: flex;
    justify-content: space-around;

    & > :not(:first-child) {
        margin-left: 20px;
    }
`;
